import { regionName as namedRegion } from "@/utils/regions";
import { percentChange } from "./format";

export const UNKNOWN_REGION = "unknown";

export const regionKey = (region: string | null): string => region ?? UNKNOWN_REGION;

export const regionCode = (key: string): string => (key === UNKNOWN_REGION ? "—" : key);

export const regionName = (key: string): string => (key === UNKNOWN_REGION ? "Unknown region" : namedRegion(key));

export type RegionMerchant = {
  regionKey: string;
  currency: string;
  currentGmv: number;
  priorGmv: number;
  factor: number | null;
};

export type RegionSummary = {
  key: string;
  currency: string;
  merchantCount: number;
  current: number;
  prior: number;
  converted: number | null;
  share: number | null;
  change: number | null;
};

type RegionEntry = Omit<RegionSummary, "key" | "share" | "change">;

export const summariseRegions = (merchants: RegionMerchant[]): RegionSummary[] => {
  const byRegion = new Map<string, RegionEntry>();
  for (const merchant of merchants) {
    const entry = byRegion.get(merchant.regionKey) ?? {
      currency: merchant.currency,
      merchantCount: 0,
      current: 0,
      prior: 0,
      converted: 0,
    };
    entry.merchantCount += 1;
    entry.current += merchant.currentGmv;
    entry.prior += merchant.priorGmv;
    entry.converted =
      entry.converted === null || merchant.factor === null
        ? null
        : entry.converted + merchant.currentGmv * merchant.factor;
    byRegion.set(merchant.regionKey, entry);
  }

  const total = [...byRegion.values()].reduce((sum, entry) => sum + (entry.converted ?? 0), 0);

  return [...byRegion.entries()]
    .map(([key, entry]) => ({
      key,
      ...entry,
      share: entry.converted === null ? null : total > 0 ? (entry.converted / total) * 100 : 0,
      change: percentChange(entry.current, entry.prior),
    }))
    .sort((a, b) => (b.converted ?? -1) - (a.converted ?? -1));
};

export type RegionOption = { key: string; merchantCount: number };

export const regionOptions = (regionKeys: string[]): RegionOption[] => {
  const counts = new Map<string, number>();
  for (const key of regionKeys) counts.set(key, (counts.get(key) ?? 0) + 1);
  return [...counts.entries()]
    .map(([key, merchantCount]) => ({ key, merchantCount }))
    .sort((a, b) => b.merchantCount - a.merchantCount || regionName(a.key).localeCompare(regionName(b.key)));
};

export const toggleRegion = (selected: string[], key: string): string[] =>
  selected.includes(key) ? selected.filter((entry) => entry !== key) : [...selected, key];

export const pickRegionFromPanel = (selected: string[], key: string): string[] =>
  selected.includes(key) ? selected.filter((entry) => entry !== key) : [key];
